import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { i18n } from '@/locales'

const routes: RouteRecordRaw[] = [
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('@/views/OnboardingView.vue'),
    meta: { titleKey: 'onboarding.label', requiresAuth: true, hideHeader: true },
  },
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeRouterView.vue'),
    meta: {
      titleKey: 'page.homeTitle',
      public: true,
    },
  },
  {
    path: '/apps',
    name: 'Apps',
    component: () => import('@/views/IntegrationsView.vue'),
    props: true,
    meta: {
      titleKey: 'page.appIntegrationTitle',
      public: true,
      hideHeader: () => {
        const urlParams = new URLSearchParams(window.location.search)
        return !!urlParams.get('user_id')
      }
    },
  },
  {
    path: '/app/:id',
    name: 'App',
    component: () => import('@/views/AppView.vue'),
    props: true,
    meta: {
      titleKey: 'page.connectIntegrationTitle',
      public: true,
      hideHeader: () => {
        const urlParams = new URLSearchParams(window.location.search)
        return !!urlParams.get('user_id')
      }
    },
  },
  {
    // App Studio apps running in the browser (FEAT-016-022)
    path: '/workspaces/:workspaceId/apps/:appId',
    name: 'WorkspaceApp',
    component: () => import('@/views/workspaceApps/WorkspaceAppView.vue'),
    props: true,
    meta: { titleKey: 'page.workspaceAppTitle', requiresAuth: true },
  },
  {
    path: '/connect/:id',
    name: 'Connect',
    component: () => import('@/views/ConnectView.vue'),
    props: true,
    meta: {
      titleKey: 'page.connectIntegrationTitle',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/connect/:id/mcp-auth',
    name: 'ConnectMcp',
    component: () => import('@/views/ConnectView.vue'),
    props: true,
    meta: {
      titleKey: 'page.connectMcpTitle',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/views/CallbackView.vue'),
    meta: {
      titleKey: 'page.connectionResultTitle',
      public: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: {
      titleKey: 'auth.login',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('@/views/auth/SignupView.vue'),
    meta: {
      titleKey: 'auth.signup',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/recovery',
    name: 'Recovery',
    component: () => import('@/views/auth/RecoveryView.vue'),
    meta: {
      titleKey: 'auth.resetPassword',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/auth/SettingsView.vue'),
    meta: {
      titleKey: 'auth.accountSettings',
      public: true,
      hideHeader: true,
    },
  },
  {
    path: '/account',
    component: () => import('@/views/AccountLayout.vue'),
    name: 'AccountLayout',
    redirect: { name: 'AccountSettings' },
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'settings',
        name: 'AccountSettings',
        component: () => import('@/views/account/AccountSettingsView.vue'),
        meta: {
          titleKey: 'account.settingsPageTitle',
        },
      },
      {
        path: 'tokens',
        name: 'AccountTokens',
        component: () => import('@/views/account/AccountTokensView.vue'),
        meta: {
          titleKey: 'account.tokensPageTitle',
        },
      },
    ],
  },
  {
    path: '/platform',
    component: () => import('@/views/PlatformAdminLayout.vue'),
    name: 'PlatformAdminLayout',
    redirect: { name: 'PlatformDashboard' },
    meta: {
      requiresAdmin: true,
    },
    children: [
      {
        path: 'dashboard',
        name: 'PlatformDashboard',
        component: () => import('@/views/platform/PlatformDashboardView.vue'),
        meta: {
          titleKey: 'platform.pageTitle',
        },
      },
      {
        path: 'organizations',
        name: 'PlatformOrganizations',
        component: () => import('@/views/platform/OrganizationListView.vue'),
        meta: {
          titleKey: 'platform.organizations.pageTitle',
        },
      },
      {
        path: 'users',
        name: 'PlatformUsers',
        component: () => import('@/views/platform/PlatformUsersView.vue'),
        props: true,
        meta: {
          titleKey: 'platform.usersPageTitle',
        },
      },
      {
        path: 'workspaces',
        name: 'PlatformWorkspaces',
        component: () => import('@/views/platform/PlatformWorkspacesView.vue'),
        meta: {
          titleKey: 'platform.workspaces.pageTitle',
        },
      },
      {
        // devices moved into the dashboard; keep old links working
        path: 'devices',
        redirect: { name: 'PlatformDashboard', query: { tab: 'devices' } },
      },
      {
        path: 'organizations/new',
        name: 'PlatformOrganizationNew',
        component: () => import('@/views/organization/OrgSettingsView.vue'),
        props: { orgId: undefined },
        meta: {
          titleKey: 'platform.organizations.editPageTitle',
        },
      },
      {
        path: 'organizations/:orgId/edit',
        name: 'PlatformOrganizationEdit',
        component: () => import('@/views/organization/OrgSettingsView.vue'),
        props: true,
        meta: {
          titleKey: 'platform.organizations.editPageTitle',
        },
      },
      {
        path: 'billing/plans',
        name: 'PlatformBillingPlans',
        component: () => import('@/views/platform/billing/PlansView.vue'),
        meta: {
          titleKey: 'platform.billing.plans.pageTitle',
        },
      },
      {
        path: 'billing/plans/:planId',
        name: 'PlatformBillingPlanEdit',
        component: () => import('@/views/platform/billing/PlanEditView.vue'),
        meta: {
          titleKey: 'platform.billing.plans.editPageTitle',
        },
      },
      {
        path: 'model-catalog',
        name: 'PlatformModelCatalog',
        component: () => import('@/views/platform/PlatformModelCatalogView.vue'),
        meta: {
          titleKey: 'platform.modelCatalogPageTitle',
        },
      },
      {
        path: 'model-prices',
        name: 'PlatformModelPrices',
        component: () => import('@/views/platform/PlatformModelPricesView.vue'),
        meta: {
          titleKey: 'platform.modelPricesPageTitle',
        },
      },
      {
        path: 'api-keys',
        name: 'PlatformApiKeys',
        component: () => import('@/views/platform/PlatformApiKeysView.vue'),
        meta: {
          titleKey: 'platform.apiKeysPageTitle',
        },
      },
      {
        path: 'billing/subscribers',
        name: 'PlatformBillingSubscribers',
        component: () => import('@/views/platform/billing/SubscribersView.vue'),
        meta: {
          titleKey: 'platform.billing.subscribers.pageTitle',
        },
      },
      {
        path: 'billing/users/:userId/subscription',
        name: 'PlatformBillingUserCredits',
        component: () => import('@/views/platform/billing/SubscriptionView.vue'),
        meta: {
          titleKey: 'platform.billing.credits.pageTitle',
        },
      },
      {
        path: 'billing/metrics',
        name: 'PlatformBillingMetrics',
        component: () => import('@/views/platform/billing/MetricsView.vue'),
        meta: {
          titleKey: 'platform.billing.metrics.pageTitle',
        },
      },
      {
        path: 'feature-flags',
        name: 'PlatformFeatureFlags',
        component: () => import('@/views/platform/FeatureFlagsView.vue'),
        meta: {
          titleKey: 'platform.featureFlagsPageTitle',
        },
      },
      {
        path: 'feature-flags/:flagKey',
        name: 'PlatformFeatureFlagEdit',
        component: () => import('@/views/platform/FeatureFlagEditView.vue'),
        meta: {
          titleKey: 'platform.featureFlagEditPageTitle',
        },
      },
      {
        path: 'auto-routing',
        name: 'PlatformAutoRouting',
        component: () => import('@/views/platform/PlatformAutoRoutingView.vue'),
        meta: {
          titleKey: 'platform.autoRouting.pageTitle',
        },
      },
      {
        path: 'connectors',
        name: 'PlatformConnectors',
        component: () => import('@/views/platform/PlatformConnectorsView.vue'),
        meta: {
          titleKey: 'platform.connectors.pageTitle',
        },
      },
      {
        path: 'connectors/:slug',
        name: 'PlatformConnectorEdit',
        component: () => import('@/views/platform/ConnectorEditView.vue'),
        meta: {
          titleKey: 'platform.connectors.editPageTitle',
        },
      },
      {
        path: 'info',
        name: 'PlatformInfo',
        component: () => import('@/views/platform/PlatformInfoView.vue'),
        meta: {
          titleKey: 'platform.info.pageTitle',
        },
      },
    ],
  },
  {
    path: '/organization/:orgId',
    component: () => import('@/views/OrgAdminLayout.vue'),
    name: 'OrgAdminLayout',
    redirect: { name: 'OrganizationDashboard' },
    props: true,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'dashboard',
        name: 'OrganizationDashboard',
        component: () => import('@/views/organization/OrgDashboardView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.pageTitle',
        },
      },
      {
        path: 'artifacts',
        name: 'OrganizationArtifacts',
        component: () => import('@/views/organization/OrgArtifactsView.vue'),
        props: true,
        meta: {
          titleKey: 'artifacts.admin.pageTitle',
        },
      },
      {
        path: 'teams',
        name: 'OrganizationTeams',
        component: () => import('@/views/organization/OrgTeamListView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.teams.pageTitle',
        },
        beforeEnter: (to, _from, next) => {
          const authStore = useAuthStore()
          const orgId = to.params.orgId as string

          // Org admin sees all teams
          const isOrgAdmin = authStore.adminOrganizations.some(org => org.id === orgId)
          if (isOrgAdmin) {
            next()
            return
          }

          // Team admin sees filtered view
          if (authStore.isTeamAdmin(orgId)) {
            next({ name: 'OrganizationTeamsAdmin', params: { orgId } })
            return
          }

          // No access
          next({ name: 'Forbidden' })
        }
      },
      {
        path: 'teams',
        name: 'OrganizationTeamsAdmin',
        component: () => import('@/views/team/TeamAdminListView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.teams.pageTitle',
        },
      },
      {
        path: 'members',
        name: 'OrganizationMembers',
        component: () => import('@/views/organization/OrgMemberListView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.members.pageTitle',
        },
      },
      {
        path: 'workspaces',
        name: 'OrganizationWorkspaces',
        component: () => import('@/views/organization/OrgWorkspacesView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.workspaces.pageTitle',
        },
      },
      {
        path: 'settings',
        name: 'OrganizationSettings',
        component: () => import('@/views/organization/OrgSettingsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.settings.pageTitle',
        },
      },
      {
        path: 'features',
        name: 'OrganizationFeatures',
        component: () => import('@/views/organization/OrgFeaturesView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.features.pageTitle',
        },
      },
      {
        path: 'models',
        name: 'OrganizationModels',
        component: () => import('@/views/organization/OrgModelsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.models.pageTitle',
        },
      },
      {
        path: 'guardrails',
        name: 'OrganizationGuardrails',
        component: () => import('@/views/organization/OrgGuardrailsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.guardrails.pageTitle',
        },
      },
      {
        path: 'integrations',
        name: 'OrganizationIntegrations',
        component: () => import('@/views/organization/OrgIntegrationsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.integrations.pageTitle',
        },
      },
      {
        path: 'quotas',
        name: 'OrganizationQuotas',
        component: () => import('@/views/organization/OrgQuotasView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.quotas.pageTitle',
        },
      },
      {
        path: 'activity',
        name: 'OrganizationActivity',
        component: () => import('@/views/organization/OrgActivityView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.activity.pageTitle',
        },
      },
      {
        path: 'analytics',
        name: 'OrganizationAnalytics',
        component: () => import('@/views/organization/OrgAnalyticsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.analytics.pageTitle',
        },
      },
      {
        path: 'ai-changes',
        name: 'OrganizationAIChanges',
        component: () => import('@/views/organization/OrgAIChangesView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.aiChanges.pageTitle',
        },
      },
      {
        path: 'violations',
        name: 'OrganizationViolations',
        component: () => import('@/views/organization/OrgViolationsView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.violations.pageTitle',
        },
      },
      {
        path: 'billing',
        name: 'OrganizationBilling',
        component: () => import('@/views/organization/OrgBillingView.vue'),
        props: true,
        meta: {
          titleKey: 'billing.pageTitle',
        },
      },
      {
        path: 'rag',
        name: 'OrganizationRag',
        component: () => import('@/views/organization/OrgRagView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.rag.pageTitle',
        },
      },
      {
        path: 'decisioning',
        name: 'OrganizationDecisioning',
        component: () => import('@/views/organization/OrgDecisioningView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.decisioning.pageTitle',
        },
      },
    ],
  },
  {
    path: '/teams/:orgId',
    name: 'TeamAdminLayout',
    component: () => import('@/views/TeamAdminLayout.vue'),
    redirect: { name: 'TeamAdminDashboard' },
    props: true,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'TeamAdminDashboard',
        component: () => import('@/views/team/TeamAdminListView.vue'),
        props: true,
        meta: {
          titleKey: 'organization.teams.pageTitle',
        },
      },
      {
        path: 'analytics',
        name: 'TeamAnalytics',
        component: () => import('@/views/team/TeamAnalyticsView.vue'),
        props: true,
        meta: {
          titleKey: 'team.analytics.pageTitle',
        },
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: {
      titleKey: 'page.accessDeniedTitle',
    },
  },
  {
    path: '/samples',
    meta: {
      public: true,
    },
    children: [
      {
        path: 'element-plus',
        name: 'ElementPlusTest',
        component: () => import('@/views/samples/ElementPlusTestView.vue'),
        meta: {
          titleKey: 'page.elementPlusTestTitle',
        },
      },
      {
        path: 'sidebar-nav',
        component: () => import('@/views/samples/SidebarNavSampleView.vue'),
        meta: {
          titleKey: 'page.sidebarNavSampleTitle',
        },
        redirect: '/samples/sidebar-nav/table',
        children: [
          {
            path: 'table',
            name: 'SampleTable',
            component: () => import('@/views/samples/SampleTableView.vue'),
          },
          {
            path: 'list',
            name: 'SampleList',
            component: () => import('@/views/samples/SampleListView.vue'),
          },
          {
            path: 'edit/:id',
            name: 'SampleEdit',
            component: () => import('@/views/samples/SampleEditView.vue'),
          },
        ],
      },
    ],
  },
  // Library landing page
  {
    path: '/library',
    name: 'Library',
    component: () => import('@/views/library/LibraryLandingView.vue'),
    meta: {
      titleKey: 'library.landing.pageTitle',
      public: true,
    },
  },
  {
    // Playbooks hub: a single top-level landing that tiles the two auth-gated
    // personal views (Published Playbooks + Shared with me), which were added
    // after the redesign and don't belong under Connectors/Marketplace. Auth
    // required (no public flag).
    path: '/library/playbooks-hub',
    name: 'Playbooks',
    component: () => import('@/views/artifacts/PlaybooksHubView.vue'),
    meta: {
      titleKey: 'artifacts.hub.pageTitle',
    },
  },
  {
    // "Shared with me": files/playbooks published to the logged-in user (FEAT-011-002
    // consumer view). Auth required — gated to logged-in users (no public flag).
    path: '/library/shared',
    name: 'SharedWithMe',
    component: () => import('@/views/artifacts/SharedWithMeView.vue'),
    meta: {
      titleKey: 'artifacts.sharedWithMe.pageTitle',
    },
  },
  // Skill Library (FEAT-006-005) - Public routes
  {
    path: '/library/skills',
    name: 'SkillLibrary',
    component: () => import('@/views/library/SkillListView.vue'),
    meta: {
      titleKey: 'library.skills.pageTitle',
      public: true,
    },
  },
  {
    path: '/library/skills/:id',
    name: 'LibrarySkill',
    component: () => import('@/views/library/SkillDetailView.vue'),
    meta: {
      titleKey: 'library.skills.skillPageTitle',
      public: true,
    },
  },
  {
    path: '/library/authors/:id',
    name: 'LibraryAuthor',
    component: () => import('@/views/library/AuthorProfileView.vue'),
    meta: {
      titleKey: 'library.authors.pageTitle',
      public: true,
    },
  },
  // Workspace Gallery routes (FEAT-006-012)
  {
    path: '/library/workspaces',
    name: 'WorkspaceLibrary',
    component: () => import('@/views/library/WorkspaceListView.vue'),
    meta: {
      titleKey: 'library.workspaces.pageTitle',
      public: true,
    },
  },
  {
    path: '/library/workspaces/:id',
    name: 'LibraryWorkspace',
    component: () => import('@/views/library/WorkspaceDetailView.vue'),
    meta: {
      titleKey: 'library.workspaces.workspacePageTitle',
      public: true,
    },
  },
  // Playbook Library routes (FEAT-006-008)
  {
    path: '/library/playbooks',
    name: 'PlaybookLibrary',
    component: () => import('@/views/library/PlaybookListView.vue'),
    meta: {
      titleKey: 'library.playbooks.pageTitle',
      public: true,
    },
  },
  {
    path: '/library/playbooks/:id',
    name: 'LibraryPlaybook',
    component: () => import('@/views/library/PlaybookDetailView.vue'),
    meta: {
      titleKey: 'library.playbooks.playbookPageTitle',
      public: true,
    },
  },
  {
    // Management page for the caller's own shared artifacts. Auth required
    // (no public flag); the static path never shadows the :id viewer below.
    path: '/library/artifacts',
    name: 'ArtifactLibrary',
    component: () => import('@/views/artifacts/ArtifactListView.vue'),
    meta: {
      titleKey: 'artifacts.list.pageTitle',
    },
  },
  {
    // Shared playbook BUNDLE viewer (multi-file). Listed before the single-file
    // :id viewer; the extra `playbooks/` segment keeps the two from colliding.
    // public:true so anonymous can view PUBLIC bundles; the backend gates
    // ORGANIZATION bundles (anonymous -> 401 -> login redirect).
    path: '/library/artifacts/playbooks/:id',
    name: 'ArtifactPlaybookViewer',
    component: () => import('@/views/artifacts/ArtifactPlaybookView.vue'),
    meta: {
      titleKey: 'artifacts.playbookViewer.pageTitle',
      public: true,
    },
  },
  {
    // Shared artifact viewer. public:true so anonymous can view PUBLIC artifacts;
    // the backend gates ORGANIZATION artifacts (anonymous -> 401 -> login redirect).
    path: '/library/artifacts/:id',
    name: 'ArtifactViewer',
    component: () => import('@/views/artifacts/ArtifactViewerView.vue'),
    meta: {
      titleKey: 'artifacts.viewer.pageTitle',
      public: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/ErrorView.vue'),
    meta: {
      titleKey: 'page.pageNotFoundTitle',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Global navigation guard for authentication, authorization, and page titles
let authInitialized = false

router.beforeEach(async (to, _from, next) => {
  // Initialize auth once
  if (!authInitialized) {
    const authStore = useAuthStore()
    await authStore.initializeAuth()
    authInitialized = true
  }

  const authStore = useAuthStore()
  const isPublic = to.meta.public === true

  // Check auth requirement
  if (!isPublic && !authStore.isAuthenticated) {
    // Preserve the intended destination so the user lands there after login
    // (HomeRouterView forwards this to the login flow / honors it once authed).
    next({ name: 'Home', query: { redirect: to.fullPath } })
    return
  }

  // Check admin requirement
  if (to.meta.requiresAdmin && !authStore.isPlatformAdmin) {
    next({ name: 'Forbidden' })
    return
  }

  // Set page title
  if (to.meta.titleKey) {
    document.title = i18n.global.t(to.meta.titleKey as string)
  } else if (to.meta.title) {
    document.title = to.meta.title as string
  }

  next()
})

// Export function to reset auth initialization (called after logout)
export const resetAuthInitialization = () => {
  authInitialized = false
}

export default router
